/* global Set */
import moment from 'moment';

/**
 * "Filter by Account Activity" (wallet transactions): audience fields matched against one wallet
 * ledger entry per recipient. Every field is optional and an unset field is
 * left out of the payload rather than sent empty. Currency, operation,
 * status, payment method, provider and movement each take several values
 * ("any of these"), sent as the list fields; a notification saved before
 * those existed holds the single field instead.
 */

/** Form field -> [list field, older single field]. */
const LIST_FIELDS = {
  walletCurrency: ['walletTransactionCurrencies', 'walletTransactionCurrency'],
  walletOperation: ['walletTransactionTypes', 'walletTransactionType'],
  walletStatus: ['walletTransactionStatuses', 'walletTransactionStatus'],
  walletPaymentMethod: [
    'walletTransactionPaymentMethods',
    'walletTransactionPaymentMethod',
  ],
  walletPaymentProvider: [
    'walletTransactionPaymentProviders',
    'walletTransactionPaymentProvider',
  ],
  walletMovement: ['walletTransactionMovements', 'walletTransactionMovement'],
};

const asList = (...values) => [
  ...new Set(
    values
      .flat()
      .filter(value => value !== null && value !== undefined && value !== ''),
  ),
];

const opts = entries => entries.map(([value, label]) => ({value, label}));

export const WALLET_CURRENCIES = opts(
  ['USD', 'EUR', 'XOF', 'XAF', 'GBP', 'NGN', 'CAD'].map(c => [c, c]),
);

export const WALLET_OPERATIONS = opts([
  ['DEPOSIT', 'Deposit'],
  ['WITHDRAWAL', 'Withdrawal'],
]);

export const WALLET_STATUSES = opts([
  ['PENDING', 'Pending'],
  ['SUCCESSFUL', 'Successful'],
  ['FAILED', 'Failed'],
  ['CANCELLED', 'Cancelled'],
]);

export const WALLET_PAYMENT_METHODS = opts([
  ['WIRE_TRANSFER', 'Wire transfer'],
  ['BANK_TRANSFER', 'Bank transfer'],
  ['MOBILE_MONEY', 'Mobile money'],
  ['HUB2_MOBILE_MONEY', 'Hub2 mobile money'],
  ['MAVIANCE_MOBILE_MONEY', 'Maviance mobile money'],
  ['CRYPTO', 'Crypto'],
  ['THIRD_PARTY_FLUTTERWAVE', 'Flutterwave (third party)'],
  ['THIRD_PARTY_PAYSTACK', 'Paystack (third party)'],
  ['THIRD_PARTY_PAYPAL', 'PayPal (third party)'],
  ['THIRD_PARTY_SVB', 'SVB (third party)'],
  ['THIRD_PARTY_REVOLUT', 'Revolut (third party)'],
  ['THIRD_PARTY_WISE', 'Wise (third party)'],
  ['THIRD_PARTY_INTERAC', 'Interac (third party)'],
]);

export const WALLET_PAYMENT_PROVIDERS = opts([
  ['DWOLLA', 'Dwolla'],
  ['FLUTTERWAVE', 'Flutterwave'],
  ['PLATFORM', 'Platform'],
  ['BANK_ACCOUNT', 'Bank account'],
  ['DABA', 'Daba'],
  ['HUB2', 'Hub2'],
  ['PAYSTACK', 'Paystack'],
  ['PAYPAL', 'PayPal'],
  ['SVB', 'SVB'],
  ['REVOLUT', 'Revolut'],
  ['WISE', 'Wise'],
  ['INTERAC', 'Interac'],
  ['APPLE', 'Apple'],
  ['MAVIANCE', 'Maviance'],
  ['GOOGLE_PAY', 'Google Pay'],
]);

export const WALLET_MOVEMENTS = opts([
  ['USER_BANK_ACCOUNT_TO_USER_WALLET', 'Bank account → wallet'],
  ['USER_WALLET_TO_USER_BANK_ACCOUNT', 'Wallet → bank account'],
  ['PLATFORM_CREDIT_USER_BALANCE', 'Platform credit to balance'],
  ['PLATFORM_DEBIT_USER_BALANCE', 'Platform debit from balance'],
  ['DEPOSIT', 'Deposit'],
  ['WITHDRAWAL', 'Withdrawal'],
  ['EXTERNAL_DEPOSIT', 'External deposit'],
]);

export const WALLET_AMOUNT_OPERATORS = opts([
  ['GREATER_THAN', '> More than'],
  ['EQUAL_TO', '= Exactly'],
  ['LESS_THAN', '< Less than'],
]);

/** A non-negative decimal with a dot, as the backend requires: `100`, `100.25`. */
export const isWalletAmount = value =>
  /^\d+(\.\d+)?$/.test(String(value ?? '').trim());

/** The wallet fields of `data.audience`, only those the admin set. */
export const buildWalletAudience = values => {
  const audience = {};
  Object.entries(LIST_FIELDS).forEach(([formField, [listField]]) => {
    const list = asList(values?.[formField]);
    if (list.length) audience[listField] = list;
  });
  // A range picker value, [from, to]; either end may be left open.
  const [from, to] = values?.walletDates || [];
  if (from || to) {
    audience.walletTransactionDateRange = {
      from: from ? moment(from).toISOString() : null,
      to: to ? moment(to).toISOString() : null,
    };
  }
  // Both halves or neither: an empty amount object is refused.
  const amount = String(values?.walletAmountValue ?? '').trim();
  if (values?.walletAmountOperator && amount) {
    audience.walletTransactionAmount = {
      operator: values.walletAmountOperator,
      value: amount,
    };
  }
  return audience;
};

/** A saved notification's wallet fields, back as form values. */
export const walletAudienceToForm = audience => {
  const range = audience?.walletTransactionDateRange;
  const lists = Object.fromEntries(
    Object.entries(LIST_FIELDS).map(([formField, [listField, singleField]]) => {
      const list = asList(audience?.[listField] ?? [], audience?.[singleField]);
      return [formField, list.length ? list : undefined];
    }),
  );
  return {
    ...lists,
    walletDates:
      range?.from || range?.to
        ? [
            range.from ? moment(range.from) : null,
            range.to ? moment(range.to) : null,
          ]
        : undefined,
    walletAmountOperator:
      audience?.walletTransactionAmount?.operator || undefined,
    walletAmountValue: audience?.walletTransactionAmount?.value || undefined,
  };
};

/** Every form field in the section, so Clear can empty them together. */
export const WALLET_FIELDS = [
  'walletCurrency',
  'walletOperation',
  'walletStatus',
  'walletPaymentMethod',
  'walletPaymentProvider',
  'walletMovement',
  'walletAmountOperator',
  'walletAmountValue',
  'walletDates',
];

const labelOf = (options, value) =>
  options.find(option => option.value === value)?.label || value;

/** "PayPal or Wise", from one or several values. */
const labelsOf = (options, values) =>
  asList(values)
    .map(value => labelOf(options, value))
    .join(' or ');

const AMOUNT_WORDS = {
  GREATER_THAN: 'more than',
  EQUAL_TO: 'exactly',
  LESS_THAN: 'less than',
};

/**
 * Who the wallet filters reach, in one sentence, or null when none is set.
 * Built from the payload itself, so it never describes a filter that is not
 * sent (half an amount, say).
 */
export const describeWalletAudience = values => {
  const audience = buildWalletAudience(values);
  if (!Object.keys(audience).length) return null;

  const entry = [
    audience.walletTransactionStatuses &&
      labelsOf(
        WALLET_STATUSES,
        audience.walletTransactionStatuses,
      ).toLowerCase(),
    audience.walletTransactionCurrencies &&
      labelsOf(WALLET_CURRENCIES, audience.walletTransactionCurrencies),
    audience.walletTransactionTypes
      ? labelsOf(
          WALLET_OPERATIONS,
          audience.walletTransactionTypes,
        ).toLowerCase()
      : 'wallet entry',
  ]
    .filter(Boolean)
    .join(' ');

  const details = [
    audience.walletTransactionPaymentMethods &&
      `paid by ${labelsOf(
        WALLET_PAYMENT_METHODS,
        audience.walletTransactionPaymentMethods,
      )}`,
    audience.walletTransactionPaymentProviders &&
      `through ${labelsOf(
        WALLET_PAYMENT_PROVIDERS,
        audience.walletTransactionPaymentProviders,
      )}`,
    audience.walletTransactionMovements &&
      `moving ${labelsOf(
        WALLET_MOVEMENTS,
        audience.walletTransactionMovements,
      )}`,
    audience.walletTransactionAmount &&
      `for ${AMOUNT_WORDS[audience.walletTransactionAmount.operator]} ${
        audience.walletTransactionAmount.value
      }`,
  ].filter(Boolean);

  const range = audience.walletTransactionDateRange;
  const day = value => moment(value).format('D MMM YYYY');
  if (range?.from && range?.to) {
    details.push(`between ${day(range.from)} and ${day(range.to)}`);
  } else if (range?.from) {
    details.push(`from ${day(range.from)}`);
  } else if (range?.to) {
    details.push(`up to ${day(range.to)}`);
  }

  return `Users with at least one ${entry}${
    details.length ? `, ${details.join(', ')}` : ''
  }.`;
};
